<template>
  <el-dialog
    :title="rule && rule.id ? `Edit rule #${rule.id}` : 'Add rule'"
    :visible.sync="syncedVisible"
    :close-on-click-modal="false"
    width="560px"
    append-to-body
    @open="init"
  >
    <el-form label-position="top" class="commission-rule-form" @submit.native.prevent="submit">
      <div class="row">
        <div class="col-sm-4">
          <el-form-item label="Procedure type">
            <el-select v-model="form.procedure_type" class="commission-rule-form__full" @change="onProcedureTypeChange">
              <el-option v-for="t in procedureTypes" :key="t.value" :label="t.label" :value="t.value" />
            </el-select>
          </el-form-item>
        </div>
        <div class="col-sm-8">
          <el-form-item label="Procedure" :error="errors.procedure">
            <el-select
              v-model="form.procedure_id"
              class="commission-rule-form__full"
              placeholder="Start typing to search"
              no-data-text="Nothing found"
              no-match-text="Nothing found"
              filterable
              remote
              :remote-method="searchProcedures"
              :loading="searching"
              @focus="onProcedureFocus"
            >
              <el-option
                v-for="p in procedureOptions"
                :key="`${p.type}-${p.id}`"
                :label="p.name ? `${p.name} (#${p.id})` : `#${p.id}`"
                :value="p.id"
              />
            </el-select>
          </el-form-item>
        </div>
      </div>

      <div class="row">
        <div class="col-sm-6">
          <el-form-item label="Commission type" :error="errors.commission_type">
            <el-select v-model="form.commission_type" class="commission-rule-form__full" @change="onCommissionTypeChange">
              <el-option v-for="t in commissionTypes" :key="t.value" :label="t.label" :value="t.value" />
            </el-select>
          </el-form-item>
        </div>
        <div class="col-sm-6">
          <el-form-item label="Value" :error="errors.commission_value">
            <el-input
              v-model.trim="form.commission_value"
              :disabled="form.commission_type === 'zero'"
              inputmode="decimal"
            >
              <template v-if="valueSuffix" slot="append">{{ valueSuffix }}</template>
            </el-input>
          </el-form-item>
        </div>
      </div>

      <div class="row">
        <div class="col-sm-6">
          <el-form-item label="Valid from" :error="errors.valid_from">
            <el-date-picker
              v-model="form.valid_from"
              type="date"
              value-format="yyyy-MM-dd"
              placeholder="Required"
              class="commission-rule-form__full"
            />
          </el-form-item>
        </div>
        <div class="col-sm-6">
          <el-form-item label="Valid to" :error="errors.valid_to">
            <el-date-picker
              v-model="form.valid_to"
              type="date"
              value-format="yyyy-MM-dd"
              placeholder="Open (no end date)"
              class="commission-rule-form__full"
            />
          </el-form-item>
        </div>
      </div>

      <div v-if="generalError" class="commission-rule-form__error">{{ generalError }}</div>
    </el-form>

    <div slot="footer" class="commission-rule-form__footer">
      <base-button type="default" @click="syncedVisible = false">Cancel</base-button>
      <base-button type="primary" :disabled="saving" @click="submit">
        {{ saving ? 'Saving...' : 'Save' }}
      </base-button>
    </div>
  </el-dialog>
</template>

<script>
import agencyAccess from '@/components/Agencies/agencyAccess';
import APIAgencies from '@/api/modules/agencies';
import { PROCEDURE_TYPES, COMMISSION_TYPES, DECIMAL_RE, violationMessage } from './commission';

const emptyForm = () => ({
  id: null,
  procedure_type: 'operation',
  procedure_id: null,
  commission_type: 'percentage',
  commission_value: '',
  valid_from: null,
  valid_to: null,
});

export default {
  mixins: [agencyAccess],
  props: {
    visible: { type: Boolean, required: true },
    agencyId: { type: Number, required: true },
    // null → create; otherwise the rule being edited.
    rule: { type: Object, default: null },
    currencyCode: { type: String, default: '' },
  },
  data: () => ({
    form: emptyForm(),
    procedureOptions: [],
    searching: false,
    searchSeq: 0,
    errors: {},
    generalError: '',
    saving: false,
    procedureTypes: PROCEDURE_TYPES,
    commissionTypes: COMMISSION_TYPES,
  }),
  computed: {
    syncedVisible: {
      get() {
        return this.visible;
      },
      set(v) {
        this.$emit('update:visible', v);
      },
    },
    valueSuffix() {
      if (this.form.commission_type === 'percentage') return '%';
      if (this.form.commission_type === 'fixed') return this.currencyCode || '';
      return '';
    },
  },
  methods: {
    init() {
      this.errors = {};
      this.generalError = '';
      const rule = this.rule;
      if (!rule) {
        this.form = emptyForm();
        this.procedureOptions = [];
        return;
      }
      const procedure = rule.procedure || {};
      this.form = {
        id: rule.id,
        procedure_type: procedure.type || 'operation',
        procedure_id: procedure.id || null,
        commission_type: rule.commission_type,
        commission_value: rule.commission_value === null || rule.commission_value === undefined
          ? ''
          : String(rule.commission_value),
        valid_from: rule.valid_from || null,
        valid_to: rule.valid_to || null,
      };
      this.procedureOptions = procedure.id
        ? [{ type: procedure.type, id: procedure.id, name: procedure.name || null }]
        : [];
    },
    onProcedureTypeChange() {
      this.form.procedure_id = null;
      this.procedureOptions = [];
    },
    onProcedureFocus() {
      if (!this.procedureOptions.length) this.searchProcedures('');
    },
    onCommissionTypeChange(type) {
      if (type === 'zero') this.form.commission_value = '0';
      else if (this.form.commission_value === '0') this.form.commission_value = '';
    },
    async searchProcedures(query) {
      const seq = ++this.searchSeq;
      this.searching = true;
      try {
        const items = await APIAgencies.searchCommissionProcedures({ type: this.form.procedure_type, q: query });
        if (seq !== this.searchSeq) return;
        // Keep the currently selected procedure in the list so its label doesn't turn into a bare id.
        const selected = this.procedureOptions.find((p) => p.id === this.form.procedure_id);
        this.procedureOptions = selected && !items.some((p) => p.id === selected.id) ? [selected, ...items] : items;
      } catch (e) {
        if (seq === this.searchSeq) this.$notify({ type: 'error', title: 'Failed to search procedures' });
      } finally {
        if (seq === this.searchSeq) this.searching = false;
      }
    },
    validate() {
      const errors = {};
      const f = this.form;

      if (!f.procedure_id) errors.procedure = 'Select a procedure';
      if (!f.commission_type) errors.commission_type = 'Select a commission type';

      if (f.commission_type !== 'zero') {
        const normalized = String(f.commission_value).replace(',', '.');
        const number = parseFloat(normalized);
        if (!DECIMAL_RE.test(normalized)) {
          errors.commission_value = 'Enter a number (max 2 decimals)';
        } else if (!(number > 0)) {
          errors.commission_value = 'Must be greater than 0';
        } else if (f.commission_type === 'percentage' && number > 100) {
          errors.commission_value = 'Must not exceed 100%';
        }
      }

      if (!f.valid_from) errors.valid_from = 'Valid from is required';
      if (f.valid_from && f.valid_to && f.valid_to < f.valid_from) {
        errors.valid_to = 'Must not be earlier than "valid from"';
      }

      return errors;
    },
    buildPayload() {
      const f = this.form;
      const payload = {
        procedure: { type: f.procedure_type, id: f.procedure_id },
        commission_type: f.commission_type,
        commission_value: f.commission_type === 'zero' ? '0' : String(f.commission_value).replace(',', '.'),
        valid_from: f.valid_from,
        valid_to: f.valid_to || null,
      };
      if (f.id) payload.id = f.id;
      return payload;
    },
    async submit() {
        if (!this.canManageAgencies) return;
      this.generalError = '';
      this.errors = this.validate();
      if (Object.keys(this.errors).length) return;

      this.saving = true;
      try {
        const saved = await APIAgencies.saveCommissionRule(this.agencyId, this.buildPayload());
        this.$emit('saved', saved);
        this.syncedVisible = false;
      } catch (e) {
        this.handleError(e);
      } finally {
        this.saving = false;
      }
    },
    handleError(e) {
      if (!e || !e.is) {
        this.generalError = 'Failed to save the rule';
        return;
      }
      if (e.is('period_overlap')) {
        const ids = e.overlappingRuleIds;
        this.generalError = ids.length
          ? `Period overlaps with rule(s) ${ids.map((id) => `#${id}`).join(', ')}`
          : 'Period overlaps with another rule for this procedure';
        return;
      }
      if (e.is('validation_failed')) {
        const fieldErrors = e.fieldErrors;
        this.errors = Object.keys(fieldErrors).reduce((acc, field) => {
          acc[field] = violationMessage(fieldErrors[field]);
          return acc;
        }, {});
        const known = ['procedure', 'commission_type', 'commission_value', 'valid_from', 'valid_to'];
        const unknown = Object.keys(this.errors).filter((f) => !known.includes(f));
        if (!Object.keys(this.errors).length || unknown.length) {
          this.generalError = unknown.length
            ? unknown.map((f) => `${f}: ${this.errors[f]}`).join('; ')
            : 'Some values are invalid';
        }
        return;
      }
      if (e.is('not_found')) {
        this.generalError = 'This rule no longer exists — reload the page';
        return;
      }
      if (e.is('agency_not_found')) {
        this.generalError = 'Partner not found';
        return;
      }
      this.generalError = `Failed to save the rule${e.code ? ` (${e.code})` : ''}`;
    },
  },
};
</script>

<style lang="scss" scoped>
.commission-rule-form {
  &__full {
    width: 100%;
  }

  &__error {
    color: #e3342f;
    font-size: 13px;
  }

  &__footer {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
  }
}
</style>
